import React, { useState, useEffect } from 'react';
import {
  Download,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Eye,
  RefreshCw,
  Filter,
  Search,
  TrendingUp,
  DollarSign,
  Users,
  Activity
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import adminWithdrawalService from '../services/adminWithdrawalService';
import WithdrawalQueue from '../components/withdrawals/WithdrawalQueue';
import WithdrawalDetails from '../components/withdrawals/WithdrawalDetails';
import WithdrawalStats from '../components/withdrawals/WithdrawalStats';
import WithdrawalActivityLogs from '../components/withdrawals/WithdrawalActivityLogs';

const WithdrawalsPage = () => {
  const [activeTab, setActiveTab] = useState('queue');
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setIsLoadingStats(true);
      const response = await adminWithdrawalService.getWithdrawalStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load withdrawal stats:', error);
      toast.error('Failed to load withdrawal statistics');
    } finally {
      setIsLoadingStats(false);
    }
  };

  const handleViewDetails = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetails = () => {
    setSelectedWithdrawal(null);
    setIsDetailsModalOpen(false);
  };

  const handleWithdrawalAction = () => {
    // Refresh stats after any withdrawal action
    loadStats();
    handleCloseDetails();
  };

  const tabs = [
    {
      id: 'queue',
      label: 'Pending Queue',
      icon: Clock,
      count: stats?.pending || 0,
      description: 'Withdrawals awaiting approval'
    },
    {
      id: 'all',
      label: 'All Withdrawals',
      icon: Download,
      count: stats?.total || 0,
      description: 'Complete withdrawal history'
    },
    {
      id: 'logs',
      label: 'Activity Logs',
      icon: Activity,
      count: null,
      description: 'Audit trail of withdrawal actions'
    },
    {
      id: 'stats',
      label: 'Statistics',
      icon: TrendingUp,
      count: null,
      description: 'Withdrawal analytics'
    }
  ];

  const quickStats = [
    {
      label: 'Pending Approvals',
      value: stats?.pending || 0,
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200'
    },
    {
      label: 'Total Completed',
      value: stats?.completed || 0,
      icon: CheckCircle2,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      label: 'Total Amount',
      value: adminWithdrawalService.formatAmount(stats?.totalAmount || 0),
      icon: DollarSign,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      label: 'Processing',
      value: stats?.processing || 0,
      icon: RefreshCw,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Withdrawal Management</h1>
          <p className="text-gray-600">Review and manage project fund withdrawals</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={loadStats}
            disabled={isLoadingStats}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 
                       bg-white border border-gray-300 rounded-lg hover:bg-gray-50 
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoadingStats ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {quickStats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`bg-white border ${stat.borderColor} rounded-lg p-4 hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
              <div className={`p-3 ${stat.bgColor} rounded-lg`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors
                             ${isActive
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.count !== null && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                                     ${isActive
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-600'
                      }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'queue' && (
            <WithdrawalQueue
              status="pending"
              onViewDetails={handleViewDetails}
              onRefresh={loadStats}
            />
          )}

          {activeTab === 'all' && (
            <WithdrawalQueue
              status="all"
              onViewDetails={handleViewDetails}
              onRefresh={loadStats}
            />
          )}

          {activeTab === 'logs' && (
            <WithdrawalActivityLogs />
          )}

          {activeTab === 'stats' && (
            <WithdrawalStats
              stats={stats}
              isLoading={isLoadingStats}
              onRefresh={loadStats}
            />
          )}
        </div>
      </div>

      {/* Withdrawal Details Modal */}
      {isDetailsModalOpen && selectedWithdrawal && (
        <WithdrawalDetails
          withdrawal={selectedWithdrawal}
          onClose={handleCloseDetails}
          onAction={handleWithdrawalAction}
        />
      )}
    </div>
  );
};

export default WithdrawalsPage;
